"use client"

import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import SignatureService from '@/api/signature.service';
import { Loader2, Plus, Mail, PenTool, Image as ImageIcon, Trash2, Edit, Star, Award } from 'lucide-react';
import { 
  Card, 
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import FormModal from '@/components/FormModal';
import { Link } from 'react-router-dom';
import CreateSignatureForm from './CreateSignatureForm';

export default function Signatures() {
  const [signatures, setSignatures] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [signatureToDelete, setSignatureToDelete] = useState<string | null>(null);
  const { toast } = useToast();
  
  const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
  const businessId = selectedBusiness?.id;
  
  useEffect(() => {
    if (businessId) {
      fetchSignatures();
    }
  }, [businessId]);
  
  const fetchSignatures = async () => {
    setIsLoading(true);
    try {
      const response = await SignatureService.getAllSignaturesForSelf();
      
      if (response.data.result === 'success') {
        setSignatures(response.data.userSignatures || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch signatures",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching signatures:", error);
      toast({
        title: "Error",
        description: "An error occurred while fetching signatures",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteSignature = async () => {
    if (!signatureToDelete) return;
    
    try {
      const response = await SignatureService.deleteSignature(signatureToDelete);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Signature deleted successfully",
        });
        fetchSignatures();
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete signature",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting signature:", error);
      toast({
        title: "Error",
        description: "An error occurred while deleting the signature",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setSignatureToDelete(null);
    }
  };
  
  const handleSetDefaultSignature = async (signatureId: string) => {
    try {
      const response = await SignatureService.setDefaultSignature(signatureId);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Default signature updated successfully",
        });
        
        // Update local state
        const updatedSignatures = signatures.map(signature => ({
          ...signature,
          isDefault: signature.publicId === signatureId
        }));
        setSignatures(updatedSignatures);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to update default signature",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error setting default signature:", error);
      toast({
        title: "Error",
        description: "An error occurred while updating default signature",
        variant: "destructive",
      });
    }
  };
  
  const getSignatureTypeIcon = (type: string) => {
    if (!type) return <Mail className="w-4 h-4 text-gray-500" />;
    
    switch (type.toLowerCase()) {
      case 'text':
        return <PenTool className="w-4 h-4 text-blue-500" />;
      case 'image':
        return <ImageIcon className="w-4 h-4 text-green-500" />;
      case 'both':
        return <Mail className="w-4 h-4 text-purple-500" />;
      default:
        return <Mail className="w-4 h-4 text-gray-500" />;
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading signatures...</p>
      </div>
    );
  }
  
  return (
    <div className="container py-6 mx-auto">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Email Signatures</h1>
            <p className="text-muted-foreground mt-1">Create and manage your email signatures</p>
          </div>
          
          <Button 
            onClick={() => setIsCreateModalOpen(true)}
            className="gap-1"
          >
            <Plus className="w-4 h-4" />
            Create Signature
          </Button>
        </div>
        
        {/* Signature List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Your Signatures</CardTitle>
            <CardDescription>
              Create professional email signatures for your campaigns
            </CardDescription>
          </CardHeader>
          <CardContent>
            {signatures.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Signatures Found</h3>
                <p className="text-muted-foreground mb-6">
                  You haven't created any email signatures yet.
                </p>
                <Button onClick={() => setIsCreateModalOpen(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Signature
                </Button>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Name</TableHead>
                      <TableHead className="w-[120px]">Type</TableHead>
                      <TableHead>Preview</TableHead>
                      <TableHead className="w-[120px]">Status</TableHead>
                      <TableHead className="w-[180px]">Created</TableHead>
                      <TableHead className="w-[100px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {signatures.map((signature) => (
                      <TableRow key={signature.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center">
                            {signature.isDefault && <Award className="w-4 h-4 text-amber-500 mr-2" />}
                            {signature.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getSignatureTypeIcon(signature.signatureType)}
                            <span>
                              {signature.signatureType === 'text' ? 'Text' : 
                               signature.signatureType === 'image' ? 'Image' : 
                               signature.signatureType === 'both' ? 'Text & Image' : 'Unknown'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Link to={`/signatures/${signature.publicId}`} className="text-primary hover:underline">
                            View Signature
                          </Link>
                        </TableCell>
                        <TableCell>
                          {signature.isDefault ? (
                            <Badge className="bg-amber-500 hover:bg-amber-600">Default</Badge>
                          ) : (
                            <Badge variant="outline">Standard</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {signature.dateCreated && format(new Date(signature.dateCreated), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="w-4 h-4"
                                >
                                  <circle cx="12" cy="12" r="1" />
                                  <circle cx="12" cy="5" r="1" />
                                  <circle cx="12" cy="19" r="1" />
                                </svg>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                className="flex items-center gap-2"
                                onClick={() => window.location.href = `/signatures/${signature.publicId}`}
                              >
                                <Mail className="w-4 h-4" />
                                View
                              </DropdownMenuItem>
                              
                              <DropdownMenuItem 
                                className="flex items-center gap-2"
                                onClick={() => window.location.href = `/signatures/edit/${signature.publicId}`}
                              >
                                <Edit className="w-4 h-4" />
                                Edit
                              </DropdownMenuItem>
                              
                              {!signature.isDefault && (
                                <DropdownMenuItem 
                                  className="flex items-center gap-2" 
                                  onClick={() => handleSetDefaultSignature(signature.publicId)}
                                >
                                  <Star className="w-4 h-4" />
                                  Set as Default
                                </DropdownMenuItem>
                              )}
                              
                              <DropdownMenuItem 
                                className="flex items-center gap-2 text-destructive focus:text-destructive"
                                onClick={() => {
                                  setSignatureToDelete(signature.publicId);
                                  setIsDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Create Signature Modal */}
      <FormModal
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        title="Create Email Signature"
      >
        <CreateSignatureForm 
          businessId={businessId} 
          onSuccess={() => {
            setIsCreateModalOpen(false);
            fetchSignatures();
          }}
        />
      </FormModal>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the signature.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSignature}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 